import React from 'react';
import { format } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import clsx from 'clsx';

const InspectionList = ({ 
  inspections, 
  onEdit, 
  onDelete, 
  onStatusChange,
  pagination,
  onPageChange 
}) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: '待下发', color: 'badge-warning' },
      processing: { label: '待处理', color: 'badge-warning' },
      reviewing: { label: '待审批', color: 'badge-primary' },
      completed: { label: '已完成', color: 'badge-success' },
      archived: { label: '已归档', color: 'badge-secondary' },
    };
    
    const config = statusConfig[status] || { label: status, color: 'badge-gray' };
    return <span className={`badge ${config.color}`}>{config.label}</span>;
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      high: { label: '高', color: 'text-red-600 bg-red-100' },
      medium: { label: '中', color: 'text-yellow-600 bg-yellow-100' },
      low: { label: '低', color: 'text-green-600 bg-green-100' },
    };
    
    const config = priorityConfig[priority] || { label: priority, color: 'text-gray-600 bg-gray-100' };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '未设置';
    try {
      return format(new Date(dateString), 'yyyy-MM-dd', { locale: zhCN });
    } catch (error) {
      return '无效日期';
    }
  };

  const getStatusOptions = (currentStatus) => {
    const statusFlow = {
      pending: ['processing', 'archived'],
      processing: ['reviewing', 'pending'],
      reviewing: ['completed', 'processing'],
      completed: ['archived'],
      archived: []
    };
    
    const options = statusFlow[currentStatus] || [];
    const optionLabels = {
      pending: '待下发',
      processing: '待处理',
      reviewing: '待审批',
      completed: '已完成',
      archived: '已归档'
    };
    
    return options.map(status => ({
      value: status,
      label: optionLabels[status] || status
    }));
  };

  return (
    <div className="bg-white shadow overflow-hidden rounded-lg">
      {inspections.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <i className="fa fa-clipboard text-gray-300 text-4xl mb-3"></i>
          <p className="text-gray-500">暂无巡检任务</p>
          <p className="text-gray-400 text-sm mt-1">点击"新建巡检任务"按钮开始创建</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    任务名称
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    巡检区域
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    优先级
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    截止时间
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    状态
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {inspections.map((inspection) => (
                  <tr key={inspection.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{inspection.title}</div>
                      <div className="text-xs text-gray-500 mt-1 truncate max-w-xs">
                        {inspection.description || '无描述'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{inspection.location}</div>
                      <div className="text-xs text-gray-500">{inspection.area}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPriorityBadge(inspection.priority)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(inspection.due_date)}
                      </div>
                      {inspection.completed_date && (
                        <div className="text-xs text-gray-500">
                          完成于: {formatDate(inspection.completed_date)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(inspection.status)}
                        {getStatusOptions(inspection.status).length > 0 && (
                          <select
                            value=""
                            onChange={(e) => {
                              if (e.target.value) {
                                onStatusChange(inspection.id, e.target.value);
                              }
                            }}
                            className="text-xs border border-gray-300 rounded px-1 py-0.5"
                          >
                            <option value="">更改状态</option>
                            {getStatusOptions(inspection.status).map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => onEdit(inspection)}
                        className="text-primary hover:text-blue-700 mr-3"
                      >
                        <i className="fa fa-edit mr-1"></i>编辑
                      </button>
                      <button
                        onClick={() => onDelete(inspection.id)}
                        className="text-danger hover:text-red-700"
                      >
                        <i className="fa fa-trash mr-1"></i>删除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 分页 */}
          {pagination && pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => onPageChange(Math.max(1, pagination.page - 1))}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  上一页
                </button>
                <button
                  onClick={() => onPageChange(Math.min(pagination.pages, pagination.page + 1))}
                  disabled={pagination.page === pagination.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  下一页
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    显示 <span className="font-medium">{(pagination.page - 1) * pagination.per_page + 1}</span> 到{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.per_page, pagination.total)}
                    </span>{' '}
                    条，共 <span className="font-medium">{pagination.total}</span> 条记录
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => onPageChange(Math.max(1, pagination.page - 1))}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">上一页</span>
                      <i className="fa fa-chevron-left"></i>
                    </button>
                    {[...Array(Math.min(5, pagination.pages))].map((_, i) => {
                      let pageNum;
                      if (pagination.pages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.pages - 2) {
                        pageNum = pagination.pages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => onPageChange(pageNum)}
                          className={clsx(
                            'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                            pagination.page === pageNum
                              ? 'z-10 bg-primary border-primary text-white'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          )}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => onPageChange(Math.min(pagination.pages, pagination.page + 1))}
                      disabled={pagination.page === pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">下一页</span>
                      <i className="fa fa-chevron-right"></i>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InspectionList;